import React, { useState } from 'react';
import { vehicleAPI, staffAPI, communityAPI, invoiceAPI } from '../services/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';

const CONFIG = {
  vehicles: {
    label: 'Vehicle',
    fields: 'plate_number, owner_name, villa_number, vehicle_type, make, vehicle_model, color, is_ev, phone_number, epc_hex',
    template: `plate_number,owner_name,villa_number,vehicle_type,make,vehicle_model,color,is_ev,phone_number,epc_hex
KA01AB1234,John Doe,A-101,4-wheeler,Honda,City,Silver,false,+91-9876543210,
KA02CD5678,Jane Smith,B-202,2-wheeler,Royal Enfield,Classic 350,Black,false,+91-9876543211,E2801170200007F0F12345AB
TS09EF9012,Raj Kumar,C-303,4-wheeler,Tesla,Model 3,White,true,+91-9876543212,E28011702000ABCDEF000111`,
    upload: (fd) => vehicleAPI.uploadCSV(fd),
    countKey: 'vehicles_added',
  },
  staff: {
    label: 'Staff',
    fields: 'name, id_number, id_type, assigned_villas, job_description, phone, emergency_contact, employee_id, gender',
    template: `name,id_number,id_type,assigned_villas,job_description,phone,emergency_contact,employee_id,gender
Mary Jane,AADHAAR123456,Aadhaar,"A-101,A-102",Housekeeping,+91-9876543210,+91-9876543211,EMP-001,female
John Worker,PAN12345,PAN Card,B-201,Driver,+91-9876543212,+91-9876543213,EMP-002,male`,
    upload: (fd) => staffAPI.uploadCSV(fd),
    countKey: 'staff_added',
  },
  users: {
    label: 'User',
    fields: 'name, email, phone, villa_number, role OR resident_type (one of: resident_owner, tenant, non_resident_owner, guard)',
    template: `name,email,role,resident_type,villa_number,phone
John Owner,john@example.com,resident_owner,,A-101,+91-9876543210
Jane Tenant,jane@example.com,,tenant,B-202,+91-9876543211
Sam NRI Owner,sam@example.com,non_resident_owner,,C-303,+91-9876543212
Ravi Guard,ravi@example.com,guard,,,+91-9876543213`,
    upload: (fd) => communityAPI.uploadCSV(fd),
    countKey: 'users_added',
  },
  invoices: {
    label: 'Invoice',
    fields: 'villa_number, amount, description, due_date, category',
    template: `villa_number,amount,description,due_date,category
A-101,5000,Monthly Maintenance - April 2026,2026-04-15,Maintenance
B-202,2500,Quarterly Water Charges,2026-04-30,Utility
C-303,1000,Penalty - Late Payment,2026-04-10,Penalty`,
    upload: (fd) => invoiceAPI.uploadCSV(fd),
    countKey: 'invoices_added',
  },
};

export default function CSVUpload({ type }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const config = CONFIG[type];
  if (!config) return null;

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await config.upload(formData);
      setResult(response.data);
      toast.success(response.data.message);
      setFile(null);
      document.getElementById(`${type}-file-input`).value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    try {
      const blob = new Blob([config.template], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${type}_template.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  return (
    <Card className="shadow-lg border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Upload {config.label} Data (CSV)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 mb-2">
              <strong>Required Fields:</strong>
            </p>
            <p className="text-xs text-blue-700">{config.fields}</p>
            <Button
              data-testid={`${type}-download-template`}
              onClick={downloadTemplate}
              size="sm"
              variant="outline"
              className="mt-2"
            >
              <FileText className="w-4 h-4 mr-1" />
              Download Template
            </Button>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Select CSV File</label>
            <input
              id={`${type}-file-input`}
              data-testid={`${type}-csv-input`}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="w-full p-2 border rounded-lg"
            />
          </div>

          <Button
            data-testid={`${type}-upload-button`}
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full bg-gk-teal hover:bg-gk-teal-hover"
          >
            {uploading ? 'Uploading...' : 'Upload CSV'}
          </Button>

          {result && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-start gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-900">{result.message}</p>
                  <p className="text-sm text-slate-600">Added: {result[config.countKey]}</p>
                </div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-3 space-y-1">
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <p className="text-sm font-semibold">Errors:</p>
                  </div>
                  {result.errors.map((error, idx) => (
                    <p key={`err-${idx}-${error.slice(0, 20)}`} className="text-xs text-red-600 ml-6">{error}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
